import React, { useState, useEffect } from 'react';
import { Play, RefreshCw, CheckCircle, AlertCircle, Clock, Building2, Users, DollarSign } from 'lucide-react';
import Button from '../../../components/ui/Button';
import salaryDistributionOrchestrationService from '../../../services/salaryDistributionOrchestrationService';
import Icon from '../../../components/AppIcon';


const SalaryDistributionOrchestrationPanel = () => {
  const [orchestrations, setOrchestrations] = useState([]);
  const [selectedOrchestration, setSelectedOrchestration] = useState(null);
  const [orchestrationDetails, setOrchestrationDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    loadOrchestrations();
  }, [filters]);

  useEffect(() => {
    if (selectedOrchestration) {
      loadOrchestrationDetails(selectedOrchestration);
    }
  }, [selectedOrchestration]);

  const loadOrchestrations = async () => {
    try {
      setLoading(true);
      const data = await salaryDistributionOrchestrationService?.getReconciliationDashboard(filters);
      setOrchestrations(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load orchestrations');
    } finally {
      setLoading(false);
    }
  };

  const loadOrchestrationDetails = async (orchestrationId) => {
    try {
      const details = await salaryDistributionOrchestrationService?.getOrchestrationDetails(orchestrationId);
      setOrchestrationDetails(details);
    } catch (err) {
      console.error('Failed to load orchestration details:', err);
    }
  };

  const handleStartDistribution = async (orchestrationId) => {
    try {
      setProcessing(true);
      await salaryDistributionOrchestrationService?.startDistribution(orchestrationId);
      await loadOrchestrations();
      if (selectedOrchestration === orchestrationId) {
        await loadOrchestrationDetails(orchestrationId);
      }
    } catch (err) {
      setError(err?.message || 'Failed to start distribution');
    } finally {
      setProcessing(false);
    }
  };

  const handleSendConfirmations = async (orchestrationId) => {
    try {
      setProcessing(true);
      const results = await salaryDistributionOrchestrationService?.sendEmployeeConfirmations(orchestrationId);
      const successCount = results?.filter(r => r?.success)?.length || 0;
      alert(`Sent ${successCount} of ${results?.length} confirmations`);
      await loadOrchestrations();
    } catch (err) {
      setError(err?.message || 'Failed to send confirmations');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-gray-100 text-gray-700', icon: Clock },
      processing: { color: 'bg-blue-100 text-blue-700', icon: RefreshCw },
      partially_completed: { color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
      completed: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
      failed: { color: 'bg-red-100 text-red-700', icon: AlertCircle }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    const Icon = config?.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${config?.color}`}>
        <Icon className="w-4 h-4" />
        {status?.replace('_', ' ')?.toUpperCase()}
      </span>
    );
  };

  const getReconciliationColor = (percentage) => {
    if (percentage >= 100) return 'text-emerald-600';
    if (percentage >= 75) return 'text-blue-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Multi-Bank Salary Distribution</h2>
          <p className="text-sm text-gray-600 mt-1">Automated salary distributions across PNG financial institutions</p>
        </div>
        <Button onClick={loadOrchestrations} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters?.status}
              onChange={(e) => setFilters({ ...filters, status: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="partially_completed">Partially Completed</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date From</label>
            <input
              type="date"
              value={filters?.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date To</label>
            <input
              type="date"
              value={filters?.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={() => setFilters({ status: '', dateFrom: '', dateTo: '' })}
              variant="outline"
              className="w-full"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Orchestrations List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orchestration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employees</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Banks</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reconciliation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Started</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {orchestrations?.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No salary distributions found</p>
                    <p className="text-sm text-gray-400 mt-1">Create a new distribution from a processed payroll run</p>
                  </td>
                </tr>
              ) : (
                orchestrations?.map((orch) => (
                  <tr
                    key={orch?.id}
                    className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                      selectedOrchestration === orch?.id ? 'bg-emerald-50' : ''
                    }`}
                    onClick={() => setSelectedOrchestration(orch?.id)}
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{orch?.orchestrationNumber}</div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(orch?.distributionStatus)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{orch?.totalEmployees}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {salaryDistributionOrchestrationService?.formatAmount(orch?.totalAmountKina)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {orch?.bankBreakdown?.map(bank => bank?.bankName?.toUpperCase())?.join(', ')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-emerald-600 h-full transition-all duration-300"
                            style={{ width: `${orch?.reconciliationProgressPercentage || 0}%` }}
                          />
                        </div>
                        <span className={`text-sm font-medium ${getReconciliationColor(orch?.reconciliationProgressPercentage)}`}>
                          {orch?.reconciliationProgressPercentage?.toFixed(0)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600">
                        {orch?.startedAt ? new Date(orch?.startedAt)?.toLocaleString() : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {orch?.distributionStatus === 'pending' && (
                        <Button
                          onClick={(e) => {
                            e?.stopPropagation();
                            handleStartDistribution(orch?.id);
                          }}
                          size="sm"
                          disabled={processing}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Start
                        </Button>
                      )}
                      {orch?.distributionStatus === 'completed' && (
                        <Button
                          onClick={(e) => {
                            e?.stopPropagation();
                            handleSendConfirmations(orch?.id);
                          }}
                          size="sm"
                          variant="outline"
                          disabled={processing}
                        >
                          Send Confirmations
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Orchestration Details */}
      {selectedOrchestration && orchestrationDetails && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Distribution Details</h3>
            <Button onClick={() => setSelectedOrchestration(null)} variant="ghost" size="sm">
              Close
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Total Employees</p>
                  <p className="text-2xl font-bold text-blue-900 mt-1">
                    {orchestrationDetails?.orchestration?.totalEmployees}
                  </p>
                </div>
                <Users className="w-8 h-8 text-blue-600 opacity-50" />
              </div>
            </div>

            <div className="bg-emerald-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-600 font-medium">Total Amount</p>
                  <p className="text-2xl font-bold text-emerald-900 mt-1">
                    {salaryDistributionOrchestrationService?.formatAmount(orchestrationDetails?.orchestration?.totalAmountKina)}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-emerald-600 opacity-50" />
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Banks</p>
                  <p className="text-2xl font-bold text-purple-900 mt-1">
                    {orchestrationDetails?.orchestration?.totalBanks}
                  </p>
                </div>
                <Building2 className="w-8 h-8 text-purple-600 opacity-50" />
              </div>
            </div>

            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600 font-medium">Reconciliation</p>
                  <p className="text-2xl font-bold text-yellow-900 mt-1">
                    {orchestrationDetails?.orchestration?.reconciliationProgressPercentage?.toFixed(0)}%
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-yellow-600 opacity-50" />
              </div>
            </div>
          </div>

          {/* Bank Batches */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3">Bank Distribution Batches</h4>
            <div className="space-y-3">
              {orchestrationDetails?.batches?.map((batch) => (
                <div key={batch?.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{batch?.bankName?.toUpperCase()}</p>
                        <p className="text-xs text-gray-500">{batch?.transferMethod?.replace('_', ' ')?.toUpperCase()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Employees</p>
                        <p className="text-sm font-medium text-gray-900">{batch?.employeeCount}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Amount</p>
                        <p className="text-sm font-medium text-gray-900">
                          {salaryDistributionOrchestrationService?.formatAmount(batch?.totalAmountKina)}
                        </p>
                      </div>
                      {getStatusBadge(batch?.batchStatus)}
                    </div>
                  </div>

                  {/* Reconciliation Status */}
                  <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-500">Reconciliation:</span>
                    <span className={`text-xs font-medium ${
                      batch?.reconciliationStatus === 'reconciled' ? 'text-emerald-600' : 'text-gray-600'
                    }`}>
                      {batch?.reconciliationStatus?.toUpperCase()}
                    </span>
                    {batch?.bankConfirmationReference && (
                      <span className="text-xs text-gray-500 ml-2">
                        Ref: {batch?.bankConfirmationReference}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Confirmations */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3">Payment Confirmations</h4>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Confirmation #</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bank</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reconciliation</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {orchestrationDetails?.confirmations?.slice(0, 10)?.map((conf) => (
                    <tr key={conf?.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{conf?.confirmationNumber}</td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{conf?.employeeName}</p>
                          <p className="text-xs text-gray-500">{conf?.employeeNumber}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{conf?.bankName?.toUpperCase()}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {salaryDistributionOrchestrationService?.formatAmount(conf?.paymentAmountKina)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          conf?.confirmationStatus === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {conf?.confirmationStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          conf?.reconciliationStatus === 'reconciled' ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {conf?.reconciliationStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {conf?.confirmationSentToEmployee ? (
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <Clock className="w-4 h-4 text-gray-400" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {orchestrationDetails?.confirmations?.length > 10 && (
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-600">
                  Showing 10 of {orchestrationDetails?.confirmations?.length} confirmations
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryDistributionOrchestrationPanel;